/**
 * uiStore — 앱 전역 UI 상태 관리 (Zustand)
 *
 * 두 가지 관심사를 하나의 스토어에서 관리한다:
 *
 * 1. **사이드바**: AdminLayout, AppLayout에서 공유하는 사이드바 열림/닫힘 상태.
 *    모바일 반응형에서 토글 버튼으로 제어한다.
 *
 * 2. **토스트**: API 호출 결과(성공/에러)를 사용자에게 피드백하는 알림.
 *    addToast() 호출 시 duration(기본 3s) 후 자동으로 제거된다.
 *    Toast 컴포넌트가 이 스토어를 구독하여 렌더링한다.
 *
 * @example
 *   // 사이드바 토글
 *   const { toggleSidebar, sidebarOpen } = useUIStore()
 *
 *   // 토스트 표시
 *   const { addToast } = useUIStore()
 *   addToast({ type: 'success', message: '저장되었습니다' })
 *   addToast({ type: 'error', message: '실패했습니다', duration: 5000 })
 */

import { create } from 'zustand'

/** 개별 토스트 알림 데이터 */
interface Toast {
  /** 고유 식별자 (crypto.randomUUID()로 자동 생성) */
  id: string
  /** 토스트 종류 — 색상/아이콘 결정에 사용 */
  type: 'success' | 'error' | 'info'
  /** 사용자에게 표시할 메시지 */
  message: string
  /** 자동 제거까지의 시간(ms). 기본 3000ms. 0이면 자동 제거 안 함 */
  duration?: number
  /** 클릭 가능한 액션 링크 (예: 생성 완료 후 "이미지 보기 →") */
  action?: {
    label: string
    href: string
  }
}

interface UIState {
  /* ── 사이드바 ── */
  sidebarOpen: boolean
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void

  /* ── 토스트 ── */
  toasts: Toast[]
  /** 토스트 추가. id를 반환하며, duration 후 자동 제거 (duration=0이면 수동 관리) */
  addToast: (toast: Omit<Toast, 'id'>) => string
  /** 기존 토스트의 내용을 업데이트 (백그라운드 진행률 갱신 등) */
  updateToast: (id: string, update: Partial<Omit<Toast, 'id'>>) => void
  /** 특정 토스트를 즉시 제거 (닫기 버튼 등) */
  removeToast: (id: string) => void
}

export const useUIStore = create<UIState>((set) => ({
  /* ── 사이드바 ── */
  sidebarOpen: true,
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  /* ── 토스트 ── */
  toasts: [],
  addToast: (toast) => {
    const id = crypto.randomUUID()
    const duration = toast.duration ?? 3000

    // 새 토스트를 배열 끝에 추가
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }))

    // duration > 0일 때만 자동 제거 (0이면 수동 관리)
    if (duration > 0) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
      }, duration)
    }

    return id
  },
  updateToast: (id, update) =>
    set((s) => ({
      toasts: s.toasts.map((t) => (t.id === id ? { ...t, ...update } : t)),
    })),
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

export type { Toast }
